import { Logo } from '@common/components/brand/Logo';
import type { Role } from '@features/auth';
import { useAuth } from '@features/auth';
import { Sidebar, useSidebar } from '@ntconsult/aluma-react';
import { useUIStore } from '@store/uiStore';
import {
  Binoculars,
  CalendarRange,
  FolderKanban,
  Gauge,
  Layers,
  Split,
  Tags,
  Users2,
  UserX,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation, useMatch } from 'react-router-dom';

type NavItem = {
  to: string;
  label: string;
  icon: React.ReactNode;
  allowedRoles?: Role[];
};

type NavSubMenu = {
  id: string;
  label: string;
  icon: React.ReactNode;
  allowedRoles?: Role[];
  children: NavItem[];
};

type NavEntry = NavItem | NavSubMenu;

const isSubMenu = (entry: NavEntry): entry is NavSubMenu => 'children' in entry;

function NavItemLink({ item }: { item: NavItem }) {
  // A raiz só fica ativa no caminho exato — senão casaria com todas as rotas.
  const end = item.to === '/';
  const match = useMatch({ path: item.to, end });
  return (
    <Sidebar.Item asChild icon={item.icon} active={!!match}>
      <NavLink to={item.to} end={end}>
        {item.label}
      </NavLink>
    </Sidebar.Item>
  );
}

function SidebarBrand() {
  const { t } = useTranslation('common');
  const { open, collapsible } = useSidebar();
  const collapsed = collapsible !== 'none' && !open;
  return (
    <Sidebar.Header className={collapsed ? 'justify-center' : undefined}>
      <Logo iconOnly size="md" />
      {!collapsed && <span className="text-base font-semibold">{t('sidebarTitle')}</span>}
    </Sidebar.Header>
  );
}

function SidebarVersion() {
  const { open, collapsible } = useSidebar();
  const collapsed = collapsible !== 'none' && !open;
  if (collapsed) return null;
  return (
    <Sidebar.Footer>
      <span className="text-xs text-foreground-subtle">v{__APP_VERSION__}</span>
    </Sidebar.Footer>
  );
}

export function AppSidebar() {
  const { t } = useTranslation('common');
  const { user } = useAuth();
  const { pathname } = useLocation();
  const sidebarFocus = useUIStore((s) => s.sidebarFocus);
  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({});
  const [handledNonce, setHandledNonce] = useState(sidebarFocus?.nonce ?? 0);
  if (sidebarFocus && sidebarFocus.nonce !== handledNonce) {
    setHandledNonce(sidebarFocus.nonce);
    setOpenMenus((prev) => ({ ...prev, [sidebarFocus.id]: true }));
  }

  const entries: NavEntry[] = [
    { to: '/projetos', label: t('nav.projects'), icon: <FolderKanban /> },
    { to: '/', label: t('nav.allocations'), icon: <Users2 /> },
    {
      id: 'checks',
      label: t('nav.checks'),
      icon: <Binoculars />,
      children: [
        { to: '/consultas/pool', label: t('nav.pool'), icon: <Layers /> },
        { to: '/consultas/sem-alocacao', label: t('nav.unallocated'), icon: <UserX /> },
        { to: '/consultas/multialocados', label: t('nav.multiAllocated'), icon: <Split /> },
        { to: '/consultas/sintese', label: t('nav.summary'), icon: <CalendarRange /> },
        {
          to: '/consultas/conferencia-percentual',
          label: t('nav.percentageCheck'),
          icon: <Gauge />,
        },
      ],
    },
    {
      to: '/tags-alocacao',
      label: t('nav.allocationTags'),
      icon: <Tags />,
      allowedRoles: ['admin'],
    },
  ];

  const canSee = (entry: NavEntry) =>
    !entry.allowedRoles ||
    entry.allowedRoles.length === 0 ||
    user?.roles.some((r) => entry.allowedRoles?.includes(r));

  // Submenu sem nenhum filho visível some junto.
  const visible = entries.flatMap((entry): NavEntry[] => {
    if (!canSee(entry)) return [];
    if (!isSubMenu(entry)) return [entry];
    const children = entry.children.filter(canSee);
    return children.length > 0 ? [{ ...entry, children }] : [];
  });

  return (
    <Sidebar ariaLabel={t('nav.sidebar')}>
      <SidebarBrand />
      <Sidebar.Nav>
        {visible.map((entry) =>
          isSubMenu(entry) ? (
            <Sidebar.SubMenu
              key={entry.id}
              label={entry.label}
              icon={entry.icon}
              open={openMenus[entry.id] ?? entry.children.some((c) => pathname.startsWith(c.to))}
              onOpenChange={(open) => setOpenMenus((prev) => ({ ...prev, [entry.id]: open }))}
            >
              {entry.children.map((item) => (
                <NavItemLink key={item.to} item={item} />
              ))}
            </Sidebar.SubMenu>
          ) : (
            <NavItemLink key={entry.to} item={entry} />
          ),
        )}
      </Sidebar.Nav>
      <SidebarVersion />
    </Sidebar>
  );
}
